import React, {useEffect, useMemo, useState} from "react";
import {
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import {getCountryForTimezone} from "countries-and-timezones";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import {
  ALL_COUNTRIES,
  filterCountries,
  resolveAllowedCountry,
} from "./countryOptions";
import useStyles from "./styles";

type Props = {
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  autoFocus?: boolean;
  defaultCountry?: CountryCode;
  /**
   * ISO-3166 alpha-2 codes the select should be limited to.
   *
   * Left undefined the component offers every country, which is right for
   * address-style inputs. Pass a list where the number has to be reachable —
   * SMS 2FA enrollment, where the backend refuses anything outside
   * `countries.sms_enabled`.
   */
  allowedCountries?: string[];
};

const isValidE164 = (value: string): boolean => {
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
};

const flagEmoji = (iso: CountryCode): string =>
  iso
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const KNOWN_COUNTRIES = new Set<string>(getCountries());

const isKnownCountry = (code?: string): code is CountryCode =>
  !!code && KNOWN_COUNTRIES.has(code);

const countryFromTimezone = (): CountryCode | undefined => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const id = tz ? getCountryForTimezone(tz)?.id : undefined;
    return isKnownCountry(id) ? id : undefined;
  } catch {
    return undefined;
  }
};

const countryFromLanguage = (): CountryCode | undefined => {
  if (typeof navigator === "undefined") return undefined;

  for (const locale of [navigator.language, ...(navigator.languages ?? [])]) {
    if (!locale) continue;

    let region: string | undefined;
    try {
      region = new Intl.Locale(locale).region ?? undefined;
    } catch {
      [, region] = locale.split("-");
    }

    const upper = region?.toUpperCase();
    if (isKnownCountry(upper)) return upper;
  }

  return undefined;
};

const inferCountry = (fallback: CountryCode): CountryCode =>
  countryFromTimezone() ?? countryFromLanguage() ?? fallback;

const PhoneNumberInput: React.FC<Props> = ({
  value,
  onChange,
  label = "Phone number",
  disabled,
  error,
  helperText,
  autoFocus,
  defaultCountry,
  allowedCountries,
}) => {
  const classes = useStyles();

  const [country, setCountry] = useState<CountryCode>(
    () => defaultCountry ?? inferCountry("GB")
  );
  const [national, setNational] = useState("");

  const options = useMemo(() => filterCountries(allowedCountries, country), [
    allowedCountries,
    country,
  ]);

  /**
   * The allowed list arrives over the network, so the country picked at mount
   * can turn out to be one we cannot deliver to. Corrected only while the field
   * is still empty — once someone has started typing, silently moving their
   * country under them would be worse than letting the backend answer.
   */
  useEffect(() => {
    if (!allowedCountries?.length || national) return;
    if (allowedCountries.includes(country)) return;

    setCountry(resolveAllowedCountry(allowedCountries, country, "GB"));
  }, [allowedCountries, country, national]);

  useEffect(() => {
    if (!value) {
      setNational("");
      return;
    }

    const digits = national.replace(/\D/g, "");
    const currentE164 = digits
      ? `+${getCountryCallingCode(country)}${digits}`
      : "";
    if (value === currentE164) return;

    const parsed = parsePhoneNumberFromString(value);
    if (!parsed) return;

    const iso =
      parsed.country ??
      ALL_COUNTRIES.find((c) => c.callingCode === parsed.countryCallingCode)
        ?.iso;
    if (iso) {
      setCountry(iso);
      setNational(new AsYouType(iso).input(parsed.nationalNumber));
    }
  }, [value, country, national]);

  const emit = (nextCountry: CountryCode, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, "");
    const e164 = digits
      ? `+${getCountryCallingCode(nextCountry)}${digits}`
      : "";
    onChange(e164, !!e164 && isValidE164(e164));
  };

  const handleCountry = (iso: CountryCode) => {
    setCountry(iso);
    emit(iso, national);
  };

  const handleNational = (raw: string) => {
    const formatted = new AsYouType(country).input(raw.replace(/\D/g, ""));
    setNational(formatted);
    emit(country, formatted);
  };

  return (
    <TextField
      label={label}
      variant="outlined"
      fullWidth
      type="tel"
      value={national}
      onChange={(e) => handleNational(e.target.value)}
      disabled={disabled}
      error={error}
      helperText={helperText}
      autoFocus={autoFocus}
      autoComplete="off"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" className={classes.adornment}>
            <Select
              className={classes.countrySelect}
              value={country}
              onChange={(e) => handleCountry(e.target.value as CountryCode)}
              disabled={disabled}
              disableUnderline
              MenuProps={{classes: {paper: classes.menuPaper}}}
              renderValue={() => country}
            >
              {options.map((c) => (
                <MenuItem
                  key={c.iso}
                  value={c.iso}
                  className={classes.menuItem}
                >
                  <span className={classes.flag}>{flagEmoji(c.iso)}</span>
                  <span className={classes.menuName}>{c.name}</span>
                  <span className={classes.menuCode}>+{c.callingCode}</span>
                </MenuItem>
              ))}
            </Select>
            <Typography className={classes.callingCode}>
              +{getCountryCallingCode(country)}
            </Typography>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default PhoneNumberInput;
